import { Injectable, OnDestroy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { FirebaseMessaging, TokenReceivedEvent } from '@capacitor-firebase/messaging';
import { Capacitor, PluginListenerHandle } from '@capacitor/core';
import * as moment from 'moment';
import { firstValueFrom, Subject } from 'rxjs';

import { environment } from '../../../../environments/environment';
import { AppSettings } from '../../../app.settings';
import { ApplicationUser } from '../../models/ApplicationUser';
import {
  FCM_INVALID_TOKEN_ERRORS,
  PUSHNOTIFCATIONS_ENDPOINTS
} from '../../models/Constants';
import { RequestService } from '../request/request.service';
import { StorageData } from '../utilities/storage-data.class';

/**
 * Interfaz para los datos enviados al backend en la sincronización de token
 */
export interface IFcmSyncPayload {
  ID_Tendero: number;
  DeviceID: string;
  DeviceIfo: string;
  PushActivo: boolean;
}

/**
 * Interfaz para actualizar el token FCM en el backend con fecha de generación
 */
export interface IFcmTokenUpdatePayload {
  ID_Tendero: number;
  DeviceID: string;
  DeviceInfo: string;
  Token: string;
  TokenFechaGeneracion: string;
}

/**
 * Servicio centralizado para la gestión de tokens FCM (Firebase Cloud Messaging).
 *
 * Responsabilidades:
 * - Obtención y renovación de tokens FCM
 * - Gestión de permisos de notificaciones push
 * - Sincronización de tokens con el backend
 * - Manejo de errores específicos de FCM
 * - Listeners para cambios de token
 *
 * @see https://firebase.google.com/docs/cloud-messaging/manage-tokens
 */
@Injectable({
  providedIn: 'root'
})
export class FcmTokenService implements OnDestroy {

  /** Subject que emite cuando el token FCM cambia */
  public tokenChanged$ = new Subject<string>();

  /** Subject que emite cuando cambia el estado del permiso de notificaciones */
  public permissionChanged$ = new Subject<boolean>();

  private tokenReceivedListener: PluginListenerHandle | null = null;

  constructor(
    private readonly requestS: RequestService,
    private readonly translate: TranslateService
  ) { }

  /** Días después de los cuales el token FCM debe renovarse (configurable vía i18n) */
  private get tokenRefreshDays(): number {
    return Number(this.translate.instant('Fcm.fcm-token-refresh-days'))
  }

  /** Días de inactividad tras los cuales el token FCM puede expirar (configurable vía i18n) */
  private get inactivityExpirationDays(): number {
    return Number(this.translate.instant('Fcm.fcm-inactivity-expiration-days'))
  }

  /** Días de inactividad a partir de los cuales se muestra advertencia de expiración */
  private get expirationWarningDays(): number {
    return this.inactivityExpirationDays - this.tokenRefreshDays
  }

  ngOnDestroy(): void {
    this.removeTokenListener();
    this.tokenChanged$.complete();
    this.permissionChanged$.complete();
  }

  /**
   * Obtiene el token FCM.
   * Maneja la diferencia entre plataformas nativas y web.
   * @returns Promise que resuelve con el token o string vacío si falla
   */
  public async getToken(): Promise<string> {
    try {
      if (!Capacitor.isNativePlatform()) {
        return await this.getWebToken();
      }
      const { token } = await FirebaseMessaging.getToken();
      return token || '';
    } catch {
      return '';
    }
  }

  /**
   * Solicita permiso y obtiene el token FCM para web.
   * @returns Promise que resuelve con el token o string vacío
   */
  private async getWebToken(): Promise<string> {
    try {
      const permissionStatus = await FirebaseMessaging.requestPermissions();
      if (permissionStatus.receive === 'granted') {
        const { token } = await FirebaseMessaging.getToken({
          vapidKey: environment.firebaseConfig.webPushKey
        });
        return token || '';
      }
      return '';
    } catch {
      return '';
    }
  }

  /**
   * Obtiene el token almacenado en el storage local.
   * @returns El token almacenado o string vacío
   */
  public async getStoredToken(): Promise<string> {
    const storage: ApplicationUser = await StorageData.getDataStorage(AppSettings.TOKEN_KEY);
    return storage?.token || '';
  }


  /**
   * Elimina el token FCM actual.
   * @returns true si se eliminó correctamente
   */
  public async deleteToken(): Promise<boolean> {
    try {
      await FirebaseMessaging.deleteToken();
      return true;
    } catch (error) {
      return false;
    }
  }

  /**
   * Verifica si el token FCM necesita renovación según las mejores prácticas de Firebase.
   * Firebase recomienda llamar getToken() mensualmente para mitigar fallos de hardware.
   *
   * @returns true si el token necesita renovación (más de 30 días)
   */
  public async needsRefresh(): Promise<boolean> {
    try {
      const storage: ApplicationUser = await StorageData.getDataStorage(AppSettings.TOKEN_KEY);

      if (!storage?.token_last_refresh) {
        return true;
      }

      const daysSinceRefresh = moment().diff(moment(storage.token_last_refresh), 'days');

      return daysSinceRefresh >= this.tokenRefreshDays;
    } catch (error) {
      return true;
    }
  }

  /**
   * Renueva el token FCM si ha pasado el tiempo recomendado (30 días).
   * Implementa la mejor práctica de Firebase de renovación mensual.
   *
   * @param forceRefresh - Si es true, renueva independientemente del tiempo transcurrido
   * @returns El token actual (renovado o existente)
   */
  public async refreshIfNeeded(forceRefresh: boolean = false): Promise<string> {
    try {
      const needsRefresh = forceRefresh || await this.needsRefresh();

      if (!needsRefresh) {
        return await this.getStoredToken();
      }

      await this.deleteToken();

      const token = await this.getToken();

      if (token) {
        await this.saveWithTimestamp(token);
        await this.syncFcmTokenWithBackend();
      }

      return token || '';
    } catch (error) {
      return '';
    }
  }

  /**
   * Calcula los días de inactividad del token FCM.
   * @returns Número de días desde la última actualización del token
   */
  public async getInactivityDays(): Promise<number> {
    try {
      const storage: ApplicationUser = await StorageData.getDataStorage(AppSettings.TOKEN_KEY);

      if (!storage?.token_last_refresh) {
        return this.inactivityExpirationDays;
      }

      return moment().diff(moment(storage.token_last_refresh), 'days');
    } catch {
      return this.inactivityExpirationDays;
    }
  }

  /**
   * Verifica si el token FCM podría estar próximo a expirar por inactividad.
   * IMPORTANTE: La política de 270 días solo aplica a Android (desde Mayo 15, 2024).
   *
   * @returns true si el token tiene más de 240 días en Android
   */
  public async isNearExpiration(): Promise<boolean> {
    if (Capacitor.getPlatform() !== 'android') {
      return false;
    }

    const inactivityDays = await this.getInactivityDays();

    if (inactivityDays >= this.expirationWarningDays) {
      return true;
    }

    return false;
  }


  /**
   * Guarda el token FCM en el storage local con timestamp de última renovación.
   * @param token - El token FCM a guardar
   */
  public async saveWithTimestamp(token?: string): Promise<void> {
    if (!token) return;

    let storage: ApplicationUser = await StorageData.getDataStorage(AppSettings.TOKEN_KEY);
    const now = new Date();

    storage = {
      ...storage,
      token,
      update_date: now,
      token_last_refresh: now
    };

    await StorageData.setDataStorage(AppSettings.TOKEN_KEY, storage);
    localStorage.setItem(AppSettings.TOKEN_KEY, JSON.stringify(storage));
  }


  /**
   * Inicializa el listener moderno para cambios de token FCM.
   * Solo disponible en Android e iOS (Capacitor no soporta este evento en web).
   */
  public async initTokenListener(): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    if (this.tokenReceivedListener) {
      return;
    }

    try {
      this.tokenReceivedListener = await FirebaseMessaging.addListener(
        'tokenReceived',
        async (event: TokenReceivedEvent) => {
          await this.saveWithTimestamp(event.token);
          this.tokenChanged$.next(event.token);
          await this.syncFcmTokenWithBackend();
        }
      );
    } catch (error) {
    }
  }

  /**
   * Remueve el listener de cambios de token FCM.
   */
  public async removeTokenListener(): Promise<void> {
    if (this.tokenReceivedListener) {
      await this.tokenReceivedListener.remove();
      this.tokenReceivedListener = null;
    }
  }


  /**
   * Verifica el permiso para recibir notificaciones push.
   * @returns true si el permiso está otorgado
   */
  public async hasPermission(): Promise<boolean> {
    try {
      if (Capacitor.isNativePlatform()) {
        const permissionStatus = await FirebaseMessaging.checkPermissions();
        return permissionStatus.receive === 'granted';
      } else {
        return Notification.permission === 'granted';
      }
    } catch {
      return false;
    }
  }

  /**
   * Solicita permiso para recibir notificaciones push.
   * @returns true si el permiso fue otorgado
   */
  public async requestPermission(): Promise<boolean> {
    try {
      const permissionStatus = await FirebaseMessaging.requestPermissions();
      const granted = permissionStatus.receive === 'granted';
      this.permissionChanged$.next(granted);
      return granted;
    } catch {
      return false;
    }
  }


  /**
   * Sincroniza el token FCM con el backend incluyendo el timestamp.
   * Solo sincroniza si el token cambió respecto al almacenado localmente.
   *
   * @param payload - Datos del usuario/dispositivo para enviar
   * @param forceSync - Si es true, sincroniza aunque el token no haya cambiado
   */
  public async syncPushPermissions(
    payload: { ID_Tendero: number; DeviceID: string; DeviceIfo: string; PushActivo: boolean },
    forceSync: boolean = false
  ): Promise<void> {
    try {
      const currentToken = await this.getToken() || await this.getStoredToken();

      if (!currentToken) {
        return;
      }

      const storedToken = await this.getStoredToken();
      const tokenChanged = storedToken !== currentToken;

      if (!forceSync && !tokenChanged) {
        return;
      }

      if (tokenChanged) {
        await this.saveWithTimestamp(currentToken);
      }

      const requestData = {
        ...PUSHNOTIFCATIONS_ENDPOINTS.post_push_permissions,
        url: `${environment.urlApiV2}${PUSHNOTIFCATIONS_ENDPOINTS.post_push_permissions.url}`,
        body: {
          ID_Tendero: payload.ID_Tendero,
          DeviceID: payload.DeviceID,
          DeviceIfo: payload.DeviceIfo,
          PushActivo: payload.PushActivo
        } as IFcmSyncPayload,
        init: { headers: { 'Authorization': 'none' } }
      };

      await firstValueFrom(this.requestS.requestHttp(requestData));
    } catch (error) {
      await this.handleSyncError(error);
    }
  }


  /**
   * Sincroniza el token FCM con el backend usando el contexto del usuario actual.
   * 
   * Este método obtiene automáticamente los datos del usuario del storage local
   * y delega a syncPushPermissions. Útil cuando se necesita sincronizar sin tener
   * acceso directo al AuthService.
   * 
   * @param idTendero ID del tendero (opcional, se obtiene del perfil si no se proporciona)
   * @param forceSync Si true, fuerza la sincronización aunque el token no haya cambiado
   */
  public async syncPushPermissionsForCurrentUser(idTendero?: number, forceSync: boolean = false): Promise<void> {
    try {
      const dataDeviceUser: ApplicationUser = await StorageData.getDataStorage(AppSettings.TOKEN_KEY);
      const profileUser = await StorageData.getDataStorage(AppSettings.PERFIL_USUARIO);

      await this.syncPushPermissions(
        {
          ID_Tendero: idTendero || (profileUser?.usr_idTendero ?? 0),
          DeviceID: dataDeviceUser?.uniqueDeviceID || '',
          DeviceIfo: dataDeviceUser?.deviceInfo || '',
          PushActivo: dataDeviceUser?.notification_permission_granted ?? false
        },
        forceSync
      );
    } catch (error) {
    }
  }

  /**
   * Actualiza el token FCM en el backend con la fecha de generación.
   * Utiliza el endpoint PUT /v1/device/fcm-token para registrar el token
   * junto con su timestamp de creación.
   *
   * @returns true si la actualización fue exitosa
   */
  public async syncFcmTokenWithBackend(): Promise<boolean> {
    try {
      const dataDeviceUser: ApplicationUser = await StorageData.getDataStorage(AppSettings.TOKEN_KEY)
      const profileUser = await StorageData.getDataStorage(AppSettings.PERFIL_USUARIO)

      const currentToken = dataDeviceUser?.token || await this.getToken()

      if (!currentToken) {
        return false
      }

      const tokenFechaGeneracion = dataDeviceUser?.token_last_refresh
        ? new Date(dataDeviceUser.token_last_refresh).toISOString()
        : new Date().toISOString()

      const payload: IFcmTokenUpdatePayload = {
        ID_Tendero: profileUser?.usr_idTendero,
        DeviceID: dataDeviceUser?.uniqueDeviceID,
        DeviceInfo: dataDeviceUser?.deviceInfo,
        Token: currentToken,
        TokenFechaGeneracion: tokenFechaGeneracion
      }

      const requestData = {
        ...PUSHNOTIFCATIONS_ENDPOINTS.put_fcm_token,
        url: `${environment.urlApiV2}${PUSHNOTIFCATIONS_ENDPOINTS.put_fcm_token.url}`,
        body: payload
      }

      await firstValueFrom(this.requestS.requestHttp(requestData))
      return true
    } catch (error) {
      return false
    }
  }

  /**
   * Maneja errores específicos de FCM retornados por el backend.
   * Si el token es inválido o no está registrado, lo elimina y genera uno nuevo.
   *
   * @param error - Error capturado de la operación FCM
   * @returns true si el error fue manejado y se generó un nuevo token
   */
  public async handleSyncError(error: any): Promise<boolean> {
    if (!Capacitor.isNativePlatform()) {
      return false;
    }

    const errorCode = error?.code || error?.error?.code || '';
    const errorMessage = error?.message || error?.error?.message || '';

    const isInvalidToken = FCM_INVALID_TOKEN_ERRORS.some(
      code => errorCode.includes(code) || errorMessage.includes(code)
    );

    if (isInvalidToken) {
      try {
        await this.deleteToken();
        const { token } = await FirebaseMessaging.getToken();

        if (token) {
          await this.saveWithTimestamp(token);
          this.tokenChanged$.next(token);
          await this.syncFcmTokenWithBackend();
          return true;
        }
      } catch (regenerateError) {
        console.error('Error al regenerar token FCM:', regenerateError);
      }
    }

    return false;
  }
}
